/**
 * 값이 비어있는지 확인
 * @param value - 확인할 값
 * @returns 값이 null, undefined, 빈 문자열, 빈 배열, 빈 객체인 경우 true
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * 값이 숫자형인지 확인
 * @param value - 확인할 값
 * @returns 값이 유효한 숫자인 경우 true
 */
export const isNumeric = (value: any): boolean => {
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value !== 'string') return false;
  return !isNaN(parseFloat(value)) && isFinite(Number(value));
};

/**
 * 값을 숫자로 변환
 * @param value - 변환할 값
 * @param defaultValue - 변환 실패시 반환할 기본값
 * @returns 변환된 숫자 또는 기본값
 */
export const toNumber = (value: any, defaultValue: number = 0): number => {
  if (value === null || value === undefined || value === '') return defaultValue;
  
  // 문자열인 경우 콤마 제거
  if (typeof value === 'string') {
    value = value.replace(/,/g, '');
  }
  
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * 이메일 유효성 검증
 * @param email - 검증할 이메일 주소
 * @returns 유효한 이메일 형식인 경우 true
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * 한글 이름 유효성 검증 (2-5자)
 * @param name - 검증할 이름
 * @returns 유효한 한글 이름인 경우 true
 */
export const isValidKoreanName = (name: string): boolean => {
  const nameRegex = /^[가-힣]{2,5}$/;
  return nameRegex.test(name);
};

/**
 * 전화번호 유효성 검증 (하이픈 무시)
 * @param phone - 검증할 전화번호
 * @returns 유효한 전화번호 형식인 경우 true
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  // 숫자만 추출
  const cleanedPhone = phone.replace(/[^0-9]/g, '');
  const phoneRegex = /^(01[016789])(\d{3,4})(\d{4})$/;
  return phoneRegex.test(cleanedPhone);
};

/**
 * 날짜 형식 유효성 검증 (YYYY-MM-DD)
 * @param dateStr - 검증할 날짜 문자열
 * @returns 유효한 날짜 형식인 경우 true
 */
export const isValidDateFormat = (dateStr: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};